/**
 * Firebase Analytics for tracking project essentials actions and CEA number.
 * Set REACT_APP_FIREBASE_* in .env to enable. CEA is stored as user property "cea_number"
 * so you can segment by user in Analytics.
 */
import { initializeApp, FirebaseApp } from 'firebase/app';
import { isDevelopment } from '../../config/env';
import { detectPeEmbedMode } from '../embedContext';
import {
  getAnalytics,
  Analytics,
  logEvent as firebaseLogEvent,
  setUserProperties,
} from 'firebase/analytics';

const PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'transformation-office';
const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
const appId = process.env.REACT_APP_FIREBASE_APP_ID;
const measurementId = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;

const firebaseConfig = apiKey && appId
  ? {
      apiKey,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || `${PROJECT_ID}.firebaseapp.com`,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || `${PROJECT_ID}.appspot.com`,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
      appId,
      ...(measurementId ? { measurementId } : {}),
    }
  : null;

let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;
let cachedCeaNumber: string | null = null;

export function initAnalytics(): void {
  if (app || !firebaseConfig) return;
  try {
    app = initializeApp(firebaseConfig);
    analytics = getAnalytics(app);
  } catch (e) {
    if (isDevelopment) {
      console.warn('Analytics init skipped', e);
    }
  }
}

function safeLogEvent(name: string, params?: Record<string, string | number | boolean>): void {
  if (!analytics) return;
  try {
    if (isDevelopment) {
      const ceaValue = typeof params?.cea_number === 'string' ? params.cea_number.trim() : '';
      console.info('[analytics]', name, params || {});
      if (!ceaValue) {
        console.warn('[analytics] missing cea_number', {
          event: name,
          params: params || {},
        });
      }
    }
    firebaseLogEvent(analytics, name, params as Record<string, unknown>);
  } catch (e) {
    if (isDevelopment) {
      console.warn('Analytics logEvent failed', name, e);
    }
  }
}

/**
 * Set CEA number as user property so Analytics reports can be segmented by user.
 */
export function setAnalyticsCea(ceaNumber: string | null | undefined): void {
  const trimmed = ceaNumber?.trim();
  if (!trimmed) return;
  cachedCeaNumber = trimmed;
  if (!analytics) return;
  try {
    setUserProperties(analytics, { cea_number: trimmed });
  } catch (e) {
    if (isDevelopment) {
      console.warn('Analytics setUserProperties failed', e);
    }
  }
}

export function getCachedAnalyticsCea(): string | null {
  return cachedCeaNumber;
}

/** Cached for the PE app lifetime — matches `PeEmbedProvider` (embed survives client navigations). */
let cachedMarketingMaterialsEmbed: boolean | null = null;

function isMarketingMaterialsEmbed(): boolean {
  if (cachedMarketingMaterialsEmbed === null) {
    cachedMarketingMaterialsEmbed = detectPeEmbedMode();
  }
  return cachedMarketingMaterialsEmbed;
}

function resolveCea(ceaNumber?: string | null): string | undefined {
  const cea = ceaNumber?.trim() || getCachedAnalyticsCea();
  return cea || undefined;
}

/**
 * 1. Top nav buttons clicked, track CEANumber as parameter, event name 'upcoming_page_clicked' etc.
 * Uses dynamic event naming based on the page key (e.g. 'upcoming_page_clicked' or 'training_page_clicked').
 */
export function logTopNavButtonClicked(pageKey: string, ceaNumber?: string | null): void {
  const normalizedKey = pageKey.trim().toLowerCase().replace(/_page$/, '');
  const eventName = `project_essential_${normalizedKey}_page_clicked`;
  const cea = ceaNumber?.trim() || getCachedAnalyticsCea();
  safeLogEvent(eventName, {
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * 2. Project details button clicked, track CEANumber, project id as parameters, event name 'project_details_page_clicked'
 */
export function logProjectDetailsPageClicked(
  projectId: string | number,
  ceaNumber?: string | null
): void {
  const cea = ceaNumber?.trim() || getCachedAnalyticsCea();
  safeLogEvent('project_essential_project_details_page_clicked', {
    project_id: String(projectId),
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * 3. Marketing materials/View project resources button clicked, track CEANumber, project id as parameters, event name 'project_resources_clicked'
 */
export function logProjectResourcesClicked(
  projectId: string | number,
  ceaNumber?: string | null
): void {
  const cea = ceaNumber?.trim() || getCachedAnalyticsCea();
  safeLogEvent('project_essential_project_resources_clicked', {
    project_id: String(projectId),
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * Log when a video is clicked.
 */
export function logVideoClicked(videoId: string | number, ceaNumber?: string | null): void {
  const cea = resolveCea(ceaNumber);
  const params = { video_id: String(videoId), ...(cea ? { cea_number: cea } : {}) };
  safeLogEvent(
    isMarketingMaterialsEmbed() ? 'marketing_materials_video_played' : 'project_essential_video_clicked',
    params,
  );
}

/**
 * Log when a video's notes download button is clicked.
 */
export function logVideoNotesDownloaded(videoId: string | number, ceaNumber?: string | null): void {
  const cea = ceaNumber?.trim() || getCachedAnalyticsCea();
  safeLogEvent('project_essential_video_notes_downloaded', {
    video_id: String(videoId),
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * Log when an EDM is clicked/previewed.
 */
export function logEdmPreviewed(edmId: string | number, ceaNumber?: string | null): void {
  const cea = resolveCea(ceaNumber);
  const id = String(edmId);
  if (isMarketingMaterialsEmbed()) {
    safeLogEvent('marketing_materials_edm_previewed', {
      file_id: id,
      ...(cea ? { cea_number: cea } : {}),
    });
    return;
  }
  safeLogEvent('project_essential_edm_previewed', {
    edm_id: id,
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * Log when an EDM is downloaded (from preview dialog or direct download).
 */
export function logEdmDownloaded(edmId: string | number, ceaNumber?: string | null): void {
  const cea = resolveCea(ceaNumber);
  const id = String(edmId);
  if (isMarketingMaterialsEmbed()) {
    safeLogEvent('marketing_materials_edm_downloaded', {
      file_id: id,
      ...(cea ? { cea_number: cea } : {}),
    });
    return;
  }
  safeLogEvent('project_essential_edm_downloaded', {
    edm_id: id,
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * Log when an eBook is clicked/previewed.
 */
export function logEbookPreviewed(ebookId: string | number, ceaNumber?: string | null): void {
  const cea = resolveCea(ceaNumber);
  const id = String(ebookId);
  if (isMarketingMaterialsEmbed()) {
    safeLogEvent('marketing_materials_ebook_previewed', {
      file_id: id,
      ...(cea ? { cea_number: cea } : {}),
    });
    return;
  }
  safeLogEvent('project_essential_ebook_previewed', {
    ebook_id: id,
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * Log when an eBook is downloaded (from preview dialog or direct download).
 */
export function logEbookDownloaded(ebookId: string | number, ceaNumber?: string | null): void {
  const cea = resolveCea(ceaNumber);
  const id = String(ebookId);
  if (isMarketingMaterialsEmbed()) {
    safeLogEvent('marketing_materials_ebook_downloaded', {
      file_id: id,
      ...(cea ? { cea_number: cea } : {}),
    });
    return;
  }
  safeLogEvent('project_essential_ebook_downloaded', {
    ebook_id: id,
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * Log when a project resource EDM is clicked/previewed.
 */
export function logProjectResourceEdmPreviewed(edmId: string | number, ceaNumber?: string | null): void {
  const cea = resolveCea(ceaNumber);
  const id = String(edmId);
  if (isMarketingMaterialsEmbed()) {
    safeLogEvent('marketing_materials_edm_previewed', {
      file_id: id,
      ...(cea ? { cea_number: cea } : {}),
    });
    return;
  }
  safeLogEvent('project_essential_project_resource_edm_previewed', {
    edm_id: id,
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * Log when a project resource EDM is downloaded.
 */
export function logProjectResourceEdmDownloaded(edmId: string | number, ceaNumber?: string | null): void {
  const cea = resolveCea(ceaNumber);
  const id = String(edmId);
  if (isMarketingMaterialsEmbed()) {
    safeLogEvent('marketing_materials_edm_downloaded', {
      file_id: id,
      ...(cea ? { cea_number: cea } : {}),
    });
    return;
  }
  safeLogEvent('project_essential_project_resource_edm_downloaded', {
    edm_id: id,
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * Log when a project resource eBook is clicked/previewed.
 */
export function logProjectResourceEbookPreviewed(ebookId: string | number, ceaNumber?: string | null): void {
  const cea = resolveCea(ceaNumber);
  const id = String(ebookId);
  if (isMarketingMaterialsEmbed()) {
    safeLogEvent('marketing_materials_ebook_previewed', {
      file_id: id,
      ...(cea ? { cea_number: cea } : {}),
    });
    return;
  }
  safeLogEvent('project_essential_project_resource_ebook_previewed', {
    ebook_id: id,
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * Log when a project resource eBook is downloaded.
 */
export function logProjectResourceEbookDownloaded(ebookId: string | number, ceaNumber?: string | null): void {
  const cea = resolveCea(ceaNumber);
  const id = String(ebookId);
  if (isMarketingMaterialsEmbed()) {
    safeLogEvent('marketing_materials_ebook_downloaded', {
      file_id: id,
      ...(cea ? { cea_number: cea } : {}),
    });
    return;
  }
  safeLogEvent('project_essential_project_resource_ebook_downloaded', {
    ebook_id: id,
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * Log when a project resource file is clicked/previewed.
 */
export function logProjectResourceFilePreviewed(fileId: string | number, ceaNumber?: string | null): void {
  const cea = ceaNumber?.trim() || getCachedAnalyticsCea();
  safeLogEvent('project_essential_project_resource_file_previewed', {
    file_id: String(fileId),
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
 * Log when a project resource file is downloaded.
 */
export function logProjectResourceFileDownloaded(fileId: string | number, ceaNumber?: string | null): void {
  const cea = ceaNumber?.trim() || getCachedAnalyticsCea();
  safeLogEvent('project_essential_project_resource_file_downloaded', {
    file_id: String(fileId),
    ...(cea ? { cea_number: cea } : {}),
  });
}
